import React from 'react';
import { observer } from 'mobx-react';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import { Formik, FormikHelpers } from 'formik';
import Typography from '@mui/material/Typography';

import { Button } from 'components/Button';

import CustomTextInputField from 'components/SharedFormComponents/CustomTextInputField';
import { useStore } from 'utils/hooks/store';
import CustomModal from 'components/CustomModal';
import {
  DetailsRowContainer,
  StyledContainer,
  TitleText,
  RequiredMark,
} from 'components/SharedFormComponents/AddTaskModal/styles';
import GroupLicenceNotice from 'components/GroupLicenceNotice';
import UserAndGroupPicker, { PickerValue } from 'components/SharedFormComponents/UserAndGroupPicker';
import TemplateSearchField from 'containers/ScheduleDetails/components/TemplateSearchField';
import { atWallClock, generateRRule, wallClockDuration } from 'containers/ScheduleDetails/utils/rruleUtils';
import { createBaseValidationSchema } from 'containers/ScheduleDetails/utils';
import { splitPickerValues } from 'containers/ScheduleDetails/utils/performers';
import { UuidableName } from 'utils/api/types';
import CustomDateTimePicker from 'components/SharedFormComponents/CustomDateTimePicker';
import { EntityModules } from 'core/constants';
import EntitySearchField from 'components/EntitySearchField';
import { timezoneStore } from 'stores/Timezone';

dayjs.extend(duration);

interface AuditFormValues {
  name: string;
  template: UuidableName | null;
  assignees: PickerValue[];
  startDate: Date | null;
  dueDate: Date | null;
  site: UuidableName | null;
  asset: UuidableName | null;
  contact: UuidableName | null;
}

const validationSchema = createBaseValidationSchema();

const nextWorkingDay = (from: Date): Date => {
  const result = new Date(from);
  do {
    result.setDate(result.getDate() + 1);
  } while (result.getDay() === 0 || result.getDay() === 6);
  return result;
};

// Pre-fill start 09:00 / due 17:00 local so a due time never silently lands at start of day, mirroring
// the recurring schedule form (ScheduleDetails.tsx). If today's 17:00 has already passed, roll to the next
// working day so the future-dated validation rule never blocks Create on a field the user never touched.
// Values stay editable.
// The same defaults on the zone's calendar, counted on a UTC date so no clock change moves the day.
const zonedDefaults = (zone: string): { startDate: Date; dueDate: Date } => {
  let day = dayjs.utc(dayjs().tz(zone).format('YYYY-MM-DD'));
  if (atWallClock(day.format('YYYY-MM-DD'), '17:00', zone).getTime() <= Date.now()) {
    do {
      day = day.add(1, 'day');
    } while (day.day() === 0 || day.day() === 6);
  }
  const date = day.format('YYYY-MM-DD');
  return { startDate: atWallClock(date, '09:00', zone), dueDate: atWallClock(date, '17:00', zone) };
};

const localDefaults = (): { startDate: Date; dueDate: Date } => {
  const todayDue = new Date();
  todayDue.setHours(17, 0, 0, 0);
  const targetDay = todayDue.getTime() > Date.now() ? new Date() : nextWorkingDay(new Date());

  const startDate = new Date(targetDay);
  startDate.setHours(9, 0, 0, 0);
  const dueDate = new Date(targetDay);
  dueDate.setHours(17, 0, 0, 0);
  return { startDate, dueDate };
};

const getInitialValues = (zone?: string): AuditFormValues => {
  const { startDate, dueDate } = zone ? zonedDefaults(zone) : localDefaults();
  return {
    name: '',
    template: null,
    assignees: [],
    startDate,
    dueDate,
    site: null,
    asset: null,
    contact: null,
  };
};

const AddAuditModal: React.FunctionComponent<{
  onClose: (created: boolean) => void;
}> = ({ onClose }): React.ReactElement => {
  const { scheduleDetails, session } = useStore();

  // An ad hoc audit takes its creator's zone, fixed when the modal opens.
  const [zone] = React.useState(() => (session.user?.timezoneModelEnabled ? timezoneStore.readerZone : undefined));
  const initialValues = React.useMemo(() => getInitialValues(zone), [zone]);

  const activeEntityModules = session.user?.activeEntityModules;

  // Ad-hoc audits are one-off schedules, so the flag-gated form source applies here too.
  const usesSubmissionForm = !!session.user?.auditsAsEntityEnabled;

  const showSites = activeEntityModules?.includes(EntityModules.Sites);
  const showAssets = activeEntityModules?.includes(EntityModules.Assets);
  const showContacts = activeEntityModules?.includes(EntityModules.Contacts);

  const handleSubmit = async (
    values: AuditFormValues,
    { setSubmitting }: FormikHelpers<AuditFormValues>
  ): Promise<void> => {
    if (!values.template || !values.startDate || !values.dueDate) {
      return;
    }

    setSubmitting(true);

    const recurrence = generateRRule('one-off', values.startDate, zone);
    const durationMs = values.dueDate.getTime() - values.startDate.getTime();
    const durationStr = zone
      ? wallClockDuration(values.startDate, values.dueDate, zone)
      : dayjs.duration(durationMs).toISOString();

    const { assignedTo, assignedGroups } = splitPickerValues(values.assignees);

    const scheduleData = {
      name: values.name,
      ...(usesSubmissionForm
        ? { submissionForm: { uuid: values.template.uuid } }
        : { template: { uuid: values.template.uuid } }),
      assignedTo: assignedTo.map((user) => ({ uuid: user.uuid })),
      ...(session.user?.groupsEnabled ? { assignedGroups: assignedGroups.map((group) => ({ uuid: group.uuid })) } : {}),
      recurrence,
      duration: durationStr,
      source: 'ad_hoc' as const,
      ...(zone && { timezone: zone }),
      ...(values.site && { site: { uuid: values.site.uuid } }),
      ...(values.asset && { asset: { uuid: values.asset.uuid } }),
      ...(values.contact && { contact: { uuid: values.contact.uuid } }),
    };

    const result = await scheduleDetails.createSchedule(scheduleData);
    if (result) {
      onClose(true);
    }
    setSubmitting(false);
  };

  return (
    <Formik<AuditFormValues> initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
      {({ values, setFieldValue, isSubmitting, isValid, dirty, errors, handleSubmit: formikHandleSubmit }) => (
        <CustomModal
          openModal
          onClose={() => onClose(false)}
          modalTitle="Create audit"
          cancelButton={
            <Button
              id="cancel-button"
              onClick={() => onClose(false)}
              variant="outlined"
              size="large"
              data-testid="cancelButton"
            >
              Cancel
            </Button>
          }
          submitButton={
            <Button
              id="create-action-button"
              onClick={() => formikHandleSubmit()}
              disabled={!isValid || !dirty || isSubmitting}
              variant="contained"
              size="large"
              data-testid="addButton"
            >
              Create audit
            </Button>
          }
        >
          <DetailsRowContainer>
            <TitleText>
              Audit Name <RequiredMark>*</RequiredMark>
            </TitleText>
            <CustomTextInputField
              value={values.name}
              onChange={(e) => setFieldValue('name', e as unknown as string)}
              description="Enter audit name"
            />
          </DetailsRowContainer>
          <DetailsRowContainer>
            <TitleText>
              Template <RequiredMark>*</RequiredMark>
            </TitleText>
            <TemplateSearchField
              value={values.template}
              onChange={(newTemplate) => setFieldValue('template', newTemplate)}
              usesSubmissionForm={usesSubmissionForm}
            />
          </DetailsRowContainer>
          {showSites && (
            <DetailsRowContainer>
              <TitleText>Site</TitleText>
              <EntitySearchField
                field="site"
                module={EntityModules.Sites}
                data={values.site}
                updateField={(_, value) => setFieldValue('site', value)}
              />
            </DetailsRowContainer>
          )}
          {showAssets && (
            <DetailsRowContainer>
              <TitleText>Asset</TitleText>
              <EntitySearchField
                field="asset"
                module={EntityModules.Assets}
                data={values.asset}
                updateField={(_, value) => setFieldValue('asset', value)}
              />
            </DetailsRowContainer>
          )}
          {showContacts && (
            <DetailsRowContainer>
              <TitleText>Contact</TitleText>
              <EntitySearchField
                field="contact"
                module={EntityModules.Contacts}
                data={values.contact}
                updateField={(_, value) => setFieldValue('contact', value)}
              />
            </DetailsRowContainer>
          )}
          <DetailsRowContainer>
            <TitleText>
              Assigned To <RequiredMark>*</RequiredMark>
            </TitleText>
            <UserAndGroupPicker
              id="audit-assignee-picker"
              data-testid="audit-assignee-picker"
              value={values.assignees}
              onChange={(next) => setFieldValue('assignees', next)}
              enableGroups={!!session.user?.groupsEnabled}
              placeholder="Select user"
              groupsPlaceholder="Choose option(s)"
              licenses="workflows"
            />
            <GroupLicenceNotice value={values.assignees} data-testid="audit-group-licence-notice" />
          </DetailsRowContainer>

          <StyledContainer>
            <DetailsRowContainer data-testid="audit-start-date-field">
              <TitleText>
                Start Date <RequiredMark>*</RequiredMark>
              </TitleText>

              <CustomDateTimePicker
                size="medium"
                value={values.startDate ? values.startDate.getTime() : null}
                onChange={(val) => {
                  setFieldValue('startDate', val ? new Date(val as string) : null);
                }}
                description=""
                timezone={zone}
              />
            </DetailsRowContainer>

            <DetailsRowContainer data-testid="audit-due-date-field">
              <TitleText>
                Due Date <RequiredMark>*</RequiredMark>
              </TitleText>
              <CustomDateTimePicker
                size="medium"
                value={values.dueDate ? values.dueDate.getTime() : null}
                onChange={(val) => {
                  setFieldValue('dueDate', val ? new Date(val as string) : null);
                }}
                description=""
                timezone={zone}
              />
              {values.dueDate && errors.dueDate && (
                <Typography variant="caption" color="error">
                  {errors.dueDate}
                </Typography>
              )}
            </DetailsRowContainer>
          </StyledContainer>
        </CustomModal>
      )}
    </Formik>
  );
};

export default observer(AddAuditModal);
